import { useCallback, useRef, useState } from "react";
import { subscriptionService } from "@miplatform/api";
import { ATTEMPTS_HUB_NAME } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import {
  SignalrHub,
  createHub,
  findHubByName,
  removeHubByName,
  subscribeToAttemptUpdate,
  subscribeToHubStateChange,
  subscribeToUserUpdate,
} from "@miplatform/signalr";
import {
  HubStatuses,
  IAttemptUpdateSubscriptionPayload,
} from "@miplatform/types";
import { bugsnag, createUUID } from "@miplatform/utils";

const startUUID = createUUID();
const stopUUID = createUUID();

export const useSignalR = ({
  onAttemptsUpdate,
  onHubStateChange,
  onUserUpdate,
  onStart,
  onStop,
  dependencies = [],
  calendarSetId,
  assignableUserIds,
  hubName = ATTEMPTS_HUB_NAME,
}: UseSignalRPayload): UseSignalRReturn => {
  const hubRef = useRef<SignalrHub>(null);
  const unsubscribeRef = useRef<() => void>(null);
  const userSignalEventRef = useRef<unknown>(null);
  const [hubState, setHubState] = useState<HubStatuses | null>(null);
  const stateManager = useComponentState();

  const getUserNegotiationOptions = useCallback(async () => {
    if (!calendarSetId) {
      return null;
    }

    const userGroups = assignableUserIds.map((r) => `users/${r}`);
    const calGroup = `calendars/${calendarSetId}`;
    const groups = [...userGroups, calGroup].join(",");
    const negotiationOptions = await subscriptionService.negotiation.users({
      data: { groups },
    });
    if (!negotiationOptions) {
      bugsnag.setContext("useSignalR::getUserNegotiationOptions");
      bugsnag.notify("Negotiation options not found");
      return null;
    }
    return negotiationOptions;
  }, [calendarSetId, assignableUserIds]);

  const onAttemptsUpdateInternal = (
    payload: IAttemptUpdateSubscriptionPayload
  ) => {
    onAttemptsUpdate?.(payload);
  };

  const onUserUpdateInternal = useCallback((events: unknown) => {
    userSignalEventRef.current = events;
    onUserUpdate?.(events);
  }, []);

  const onHubStateChangeInternal = useCallback((state: HubStatuses) => {
    setHubState(state);

    onHubStateChange?.(state);
  }, []);

  const subscribe = useCallback(() => {
    let subscriptions: { unsubscribe: () => void }[];

    if (hubRef.current) {
      subscriptions = [
        subscribeToHubStateChange(hubRef.current, onHubStateChangeInternal),
        subscribeToUserUpdate(hubRef.current, onUserUpdateInternal),
        subscribeToAttemptUpdate(hubRef.current, onAttemptsUpdateInternal),
      ];
    }

    return () => {
      if (subscriptions) {
        subscriptions.forEach((subs) => {
          if (subs && typeof subs.unsubscribe === "function") {
            subs.unsubscribe();
          }
        });
      }
    };
  }, []);

  const start = useCallback(() => {
    stateManager.abort(startUUID);
    return stateManager.run(async () => {
      if (!calendarSetId) {
        return;
      }

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }

      hubRef.current = findHubByName(hubName) ?? null;

      if (!hubRef.current) {
        const options = await getUserNegotiationOptions();

        if (!options) {
          bugsnag.setContext("useSignalR::start");
          bugsnag.notify(
            `Failed to get options for assignable users in calendar ${calendarSetId}`
          );
          return;
        }

        hubRef.current = createHub(
          hubName,
          options?.data.url?.replace("signalr-emulator", "localhost"),
          {
            accessTokenFactory: async () => {
              const opts = await getUserNegotiationOptions();
              return opts?.data?.accessToken;
            },
          },
          true
        );
      }
      hubRef.current.start();
      onStart?.();
      unsubscribeRef.current = subscribe();
    }, startUUID);
  }, []);

  const stop = useCallback(() => {
    stateManager.abort(stopUUID);
    return stateManager.run(() => {
      if (!calendarSetId) {
        return;
      }

      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      if (hubRef.current) {
        hubRef.current.stop?.();
        removeHubByName(hubName);
        hubRef.current = null;
      }

      setHubState(HubStatuses.Disconnected);

      onStop?.();
    }, stopUUID);
  }, []);

  return {
    start,
    stop,
    connectionStatus: hubState ?? HubStatuses.Disconnected,
    isLoading: stateManager.isBusy(),
  };
};

type UseSignalRPayload = {
  onAttemptsUpdate?: (payload: IAttemptUpdateSubscriptionPayload) => void;
  onUserUpdate?: (payload: unknown) => void;
  onHubStateChange?: (payload: HubStatuses) => void;
  onStart?: () => void;
  onStop?: () => void;
  dependencies?: unknown[];
  assignableUserIds: string[];
  calendarSetId: string | null;
  hubName?: string;
};

type UseSignalRReturn = {
  start: () => void;
  stop: () => void;
  connectionStatus: HubStatuses;
  isLoading: boolean;
};
